import React, { useMemo, useState } from 'react'
import {
  DetailCard,
  DetailCardGrid,
  type DetailCardRow,
  NavList,
  NAV_LIST_DEMO_ITEMS,
  NAV_LIST_PREVIEW_STATE_LABELS,
  NAV_LIST_PREVIEW_STATES,
  type NavListPreviewState,
} from '@enamelai/design-system'
import UiPropertiesPanel, {
  type UiPropertySection,
} from '../components/UiPropertiesPanel'
import UiPreviewCanvas from '../components/UiPreviewCanvas'
import './NavListShowcase.css'

type NetworkSample = {
  tag: string
  individual: DetailCardRow[]
  family: DetailCardRow[]
}

const NETWORK_SAMPLES: Record<string, NetworkSample> = {
  ppo: {
    tag: 'In-network',
    individual: [
      { label: 'Deductible', value: '$50.00', met: true },
      { label: 'Annual maximum', value: '$1,500.00 / $2,000.00' },
      { label: 'Remaining', value: '$500.00', highlight: true },
    ],
    family: [
      { label: 'Deductible', value: '$150.00' },
      { label: 'Annual maximum', value: 'None', muted: true },
    ],
  },
  premier: {
    tag: 'In-network',
    individual: [
      { label: 'Deductible', value: '$25.00' },
      { label: 'Annual maximum', value: '$900.00 / $1,500.00' },
      { label: 'Remaining', value: '$600.00', highlight: true },
    ],
    family: [
      { label: 'Deductible', value: '$75.00', met: true },
      { label: 'Annual maximum', value: '$2,100.00 / $3,000.00' },
      { label: 'Remaining', value: '$900.00', highlight: true },
    ],
  },
  oon: {
    tag: 'Out-of-network',
    individual: [
      { label: 'Deductible', value: '$100.00' },
      { label: 'Annual maximum', value: '$400.00 / $1,000.00' },
      { label: 'Remaining', value: '$600.00', highlight: true },
    ],
    family: [
      { label: 'Deductible', value: '$300.00' },
      { label: 'Annual maximum', value: 'None', muted: true },
    ],
  },
}

const NavListShowcase: React.FC = () => {
  const [title, setTitle] = useState('Networks')
  const [showChevron, setShowChevron] = useState(true)
  const [showTitle, setShowTitle] = useState(true)
  const [selectedId, setSelectedId] = useState(NAV_LIST_DEMO_ITEMS[0].id)
  const [highlightedState, setHighlightedState] =
    useState<NavListPreviewState>('default')

  const selectedItem =
    NAV_LIST_DEMO_ITEMS.find((item) => item.id === selectedId) ??
    NAV_LIST_DEMO_ITEMS[0]
  const selectedSample =
    NETWORK_SAMPLES[selectedId] ?? NETWORK_SAMPLES[NAV_LIST_DEMO_ITEMS[0].id]

  const propertySections: UiPropertySection[] = useMemo(
    () => [
      {
        title: 'Appearance',
        fields: [
          {
            id: 'nav-list-highlight',
            label: 'Highlighted state',
            control: (
              <select
                id="nav-list-highlight"
                className="ui-field-control"
                value={highlightedState}
                onChange={(event) =>
                  setHighlightedState(event.target.value as NavListPreviewState)
                }
              >
                {NAV_LIST_PREVIEW_STATES.map((option) => (
                  <option key={option} value={option}>
                    {NAV_LIST_PREVIEW_STATE_LABELS[option]}
                  </option>
                ))}
              </select>
            ),
          },
          {
            id: 'nav-list-title',
            label: 'Title',
            control: (
              <input
                id="nav-list-title"
                className="ui-field-control"
                type="text"
                value={title}
                onChange={(event) => setTitle(event.target.value)}
                disabled={!showTitle}
              />
            ),
          },
          {
            id: 'nav-list-show-title',
            label: 'Show title',
            control: (
              <label className="ui-field-checkbox">
                <input
                  id="nav-list-show-title"
                  type="checkbox"
                  checked={showTitle}
                  onChange={(event) => setShowTitle(event.target.checked)}
                />
                <span>Enabled</span>
              </label>
            ),
          },
          {
            id: 'nav-list-chevron',
            label: 'Show chevron',
            control: (
              <label className="ui-field-checkbox">
                <input
                  id="nav-list-chevron"
                  type="checkbox"
                  checked={showChevron}
                  onChange={(event) => setShowChevron(event.target.checked)}
                />
                <span>Enabled</span>
              </label>
            ),
          },
        ],
      },
    ],
    [highlightedState, showChevron, showTitle, title],
  )

  return (
    <>
      <UiPreviewCanvas
        title="Nav list"
        description="Vertical selectable list with optional section title and chevron — as used for Networks in Patient Profile → Insurance Summary."
        stageClassName="ui-preview__stage--wide nav-list-showcase__stage-wrap"
      >
        <div className={`nav-list-showcase__sheet`}>
          <section className={`ui-showcase-live`}>
            <h3 className="ui-showcase-live__title">Live preview</h3>
            <p className="ui-showcase-live__note">
              Click a network to switch the plan details on the right.
            </p>
            <div className={`nav-list-showcase__live-layout`}>
              <div className="nav-list-showcase__live-nav">
                <NavList
                  title={showTitle ? title : undefined}
                  items={NAV_LIST_DEMO_ITEMS}
                  value={selectedId}
                  onChange={setSelectedId}
                  showChevron={showChevron}
                />
              </div>
              <div className="nav-list-showcase__live-panel">
                <header className="nav-list-showcase__panel-header">
                  <h4 className="nav-list-showcase__panel-title">
                    {selectedItem.label}
                  </h4>
                  <span className="nav-list-showcase__panel-tag">
                    {selectedSample.tag}
                  </span>
                </header>
                <p className="nav-list-showcase__panel-subtitle">
                  Deductible & annual maximum for the selected network.
                </p>
                <DetailCardGrid>
                  <DetailCard
                    title="Individual"
                    rows={selectedSample.individual}
                  />
                  <DetailCard title="Family" rows={selectedSample.family} />
                </DetailCardGrid>
              </div>
            </div>
          </section>

          <section className="nav-list-showcase__demo">
            <h3 className="ui-showcase-live__title">
              Insurance Summary — Networks
            </h3>
            <p className="ui-showcase-live__note">
              Standalone list matching the Patient Profile networks column
              (~210px).
            </p>
            <div className="nav-list-showcase__demo-nav">
              <NavList
                title="Networks"
                items={NAV_LIST_DEMO_ITEMS}
                defaultValue="oon"
              />
            </div>
          </section>

          <section className="nav-list-showcase__matrix">
            {NAV_LIST_PREVIEW_STATES.map((previewState) => (
              <div
                key={previewState}
                className={`nav-list-showcase__row${
                  highlightedState === previewState
                    ? ' nav-list-showcase__row--selected'
                    : ''
                }`}
              >
                <h4 className="nav-list-showcase__label">
                  {NAV_LIST_PREVIEW_STATE_LABELS[previewState]}
                </h4>
                <NavList previewState={previewState} />
              </div>
            ))}
          </section>
        </div>
      </UiPreviewCanvas>

      <UiPropertiesPanel componentName="NavList" sections={propertySections} />
    </>
  )
}

export default NavListShowcase
